import { apiClient } from './config.js';
import type {
  PaginatedResponse,
  Entity,
  EntitySummary,
  EntityDetail,
  EntityCreateRequest,
  EntityUpdateRequest,
  EntitySourceMapping,
  SourceMappingCreateRequest,
  SourceMappingUpdateRequest,
  SourceTestResult,
  EntityListParams,
} from './types.js';

/**
 * List entities with filtering and pagination (Admin only)
 */
export async function listEntities(
  params?: EntityListParams
): Promise<PaginatedResponse<EntitySummary>> {
  const response = await apiClient.get<PaginatedResponse<EntitySummary>>(
    '/admin/entities',
    { params }
  );

  return response.data;
}

/**
 * Create a new entity (Admin only)
 */
export async function createEntity(
  data: EntityCreateRequest
): Promise<Entity> {
  const response = await apiClient.post<Entity>('/admin/entities', data);
  return response.data;
}

/**
 * Get entity details (Admin only)
 */
export async function getEntity(entityId: string): Promise<EntityDetail> {
  const response = await apiClient.get<EntityDetail>(
    `/admin/entities/${entityId}`
  );
  return response.data;
}

/**
 * Update entity (Admin only)
 */
export async function updateEntity(
  entityId: string,
  data: EntityUpdateRequest
): Promise<Entity> {
  const response = await apiClient.put<Entity>(
    `/admin/entities/${entityId}`,
    data
  );
  return response.data;
}

/**
 * Delete entity (Admin only)
 * Checks for dependencies before deletion
 */
export async function deleteEntity(entityId: string): Promise<void> {
  await apiClient.delete(`/admin/entities/${entityId}`);
}

// ============================================================================
// Source Mapping Management
// ============================================================================

/**
 * List source mappings for an entity (Admin only)
 */
export async function listSourceMappings(
  entityId: string
): Promise<EntitySourceMapping[]> {
  const response = await apiClient.get<EntitySourceMapping[]>(
    `/admin/entities/${entityId}/source-mappings`
  );
  return response.data;
}

/**
 * Create source mapping for an entity (Admin only)
 */
export async function createSourceMapping(
  entityId: string,
  data: SourceMappingCreateRequest
): Promise<EntitySourceMapping> {
  const response = await apiClient.post<EntitySourceMapping>(
    `/admin/entities/${entityId}/source-mappings`,
    data
  );
  return response.data;
}

/**
 * Update source mapping (Admin only)
 */
export async function updateSourceMapping(
  entityId: string,
  mappingId: string,
  data: SourceMappingUpdateRequest
): Promise<EntitySourceMapping> {
  const response = await apiClient.put<EntitySourceMapping>(
    `/admin/entities/${entityId}/source-mappings/${mappingId}`,
    data
  );
  return response.data;
}

/**
 * Delete source mapping (Admin only)
 */
export async function deleteSourceMapping(
  entityId: string,
  mappingId: string
): Promise<void> {
  await apiClient.delete(
    `/admin/entities/${entityId}/source-mappings/${mappingId}`
  );
}

/**
 * Test source mapping (Admin only)
 * Triggers a test fetch to verify source connection
 *
 * NOTE: Currently returns mock data (Week 6 implementation)
 */
export async function testSourceMapping(
  entityId: string,
  mappingId: string
): Promise<SourceTestResult> {
  const response = await apiClient.post<SourceTestResult>(
    `/admin/entities/${entityId}/source-mappings/${mappingId}/test`
  );
  return response.data;
}

// Discovery & Enrichment

export async function lookupEntity(query: string): Promise<Record<string, any>> {
  const { data } = await apiClient.get('/admin/entities/lookup', { params: { query } });
  return data;
}

export async function enrichEntity(entityId: string): Promise<Record<string, any>> {
  const { data } = await apiClient.post(`/admin/entities/${entityId}/enrich`);
  return data;
}

/**
 * Backfill cynapse_entity_id for entities currently unbound (Admin only).
 *
 * Runs server-side — assayer-backend hits Cynapse internally (VPC), which a
 * laptop-side script cannot do (Cynapse Cloud Run ingress is
 * internal-and-cloud-load-balancing).
 *
 * Use case: FR mig 145 (entities.cynapse_entity_id NOT NULL) requires all
 * entities to be bound. Many production entities are foreign-exchange
 * tickers / ETFs / service firms that legitimately can't resolve — those
 * return no_match and stay NULL.
 */
export interface BackfillCynapseBindingResult {
  stats: {
    processed: number;
    bound: number;
    no_match: number;
    error: number;
    skipped: number;
  };
  examples: {
    bound: Array<{ entity_id: string; display_name: string; cynapse_entity_id: string }>;
    no_match: Array<{ entity_id: string; display_name: string; canonical_identifier?: string }>;
    error: Array<{ entity_id: string; display_name: string; error?: string }>;
  };
  error?: string;
}

export async function backfillCynapseBinding(params?: {
  dry_run?: boolean;
  limit?: number;
  entity_type?: string;
}): Promise<BackfillCynapseBindingResult> {
  const { data } = await apiClient.post<BackfillCynapseBindingResult>(
    '/admin/entities/backfill-cynapse-binding',
    null,
    { params }
  );
  return data;
}

/**
 * Enqueue every unbound entity for background sync. Instant SQL flip
 * (cynapse_sync_status='pending' across all NULL-binding rows).
 * Sync workers drain the queue at their own pace — Run All -safe.
 * Added 2026-06-08 for FR mig 145 unblock + fund-shape Layer 1 extension.
 */
export interface EnqueueCynapseBindingResult {
  enqueued: number;
  by_type: Record<string, number>;
  note: string;
}

export async function enqueueCynapseBinding(params?: {
  entity_type?: string;
}): Promise<EnqueueCynapseBindingResult> {
  const { data } = await apiClient.post<EnqueueCynapseBindingResult>(
    '/admin/entities/backfill-cynapse-binding-enqueue',
    null,
    { params }
  );
  return data;
}

/** Live progress counters for the Cynapse binding pipeline. */
export interface CynapseBindingProgress {
  total: number;
  synced: number;
  pending: number;
  syncing: number;
  failed: number;
  merged: number;
  unbindable: number;
  by_type_still_unbound: Record<string, number>;
}

export async function getCynapseBindingProgress(): Promise<CynapseBindingProgress> {
  const { data } = await apiClient.get<CynapseBindingProgress>(
    '/admin/entities/backfill-cynapse-binding-progress',
  );
  return data;
}

/**
 * Entity drift sweep — surfaces MM8-class entities for operator review.
 * Walks entity_local_state vs latest announcement.company_name; queues
 * each divergence into Cynapse's attribute_change_candidates.
 * FR Phase 2 — Bucket B Gap 4.
 */
export interface DriftSweepResult {
  stats: {
    scanned: number;
    drifted: number;
    queued: number;
    duplicate: number;
    error: number;
    skipped_no_cynapse_id: number;
  };
  examples: Array<{
    assayer_entity_id: string;
    cynapse_entity_id: string | null;
    stale_name: string;
    current_name: string;
    source_announcement_id: string | null;
  }>;
  error?: string;
}

export interface DriftSweepProgress {
  drifted: number;
  drifted_with_cynapse_id: number;
  drifted_no_cynapse_id: number;
}

export async function triggerDriftSweep(params?: {
  dry_run?: boolean;
  limit?: number;
}): Promise<DriftSweepResult> {
  const { data } = await apiClient.post<DriftSweepResult>(
    '/admin/entities/drift-sweep',
    null,
    { params },
  );
  return data;
}

export async function getDriftSweepProgress(): Promise<DriftSweepProgress> {
  const { data } = await apiClient.get<DriftSweepProgress>(
    '/admin/entities/drift-sweep-progress',
  );
  return data;
}

/**
 * Step B — bulk canonical normalisation. Strips legal-form suffixes
 * from entity display_names per jurisdiction; records originals as
 * `legal_name` aliases. Driven by /tools card.
 */
export interface CanonicalNormalisationResult {
  stats: {
    scanned: number;
    drifted: number;
    applied: number;
    duplicate: number;
    error: number;
    skipped_no_cynapse_id: number;
    skipped_no_change: number;
  };
  examples: Array<{
    assayer_entity_id: string;
    cynapse_entity_id: string | null;
    display_name: string;
    stripped: string;
    entity_type: string | null;
    exchange: string | null;
  }>;
  error?: string;
}

export interface CanonicalNormalisationProgress {
  total: number;
  needs_cleanup: number;
  needs_cleanup_with_cynapse_id: number;
  needs_cleanup_no_cynapse_id: number;
}

export async function triggerCanonicalNormalisation(params?: {
  dry_run?: boolean;
  limit?: number;
}): Promise<CanonicalNormalisationResult> {
  const { data } = await apiClient.post<CanonicalNormalisationResult>(
    '/admin/entities/canonical-normalisation',
    null,
    { params },
  );
  return data;
}

export async function getCanonicalNormalisationProgress(): Promise<CanonicalNormalisationProgress> {
  const { data } = await apiClient.get<CanonicalNormalisationProgress>(
    '/admin/entities/canonical-normalisation-progress',
  );
  return data;
}

/**
 * Entity external codes backfill — populates Cynapse's
 * entity_external_codes table from Assayer's ticker store. Pre-condition
 * for the Entity Mismatch Audit (FR plan L515-527). Cuts off the
 * name-fallback path in the Cynapse resolver that enabled the MM8/MRT
 * contamination class. See project_entity_external_codes_gap.
 */
export interface ExternalCodesBackfillResult {
  stats: {
    scanned: number;
    candidates: number;
    created: number;
    already_present: number;
    error: number;
    skipped_no_cynapse_id: number;
    skipped_no_ticker: number;
  };
  examples: Array<{
    assayer_entity_id: string;
    cynapse_entity_id: string;
    ticker: string;
    display_name: string | null;
    entity_type: string;
  }>;
  error?: string;
}

export interface ExternalCodesBackfillProgress {
  total: number;
  has_ticker: number;
  has_cynapse: number;
  backfillable: number;
}

export async function triggerExternalCodesBackfill(params?: {
  dry_run?: boolean;
  limit?: number;
}): Promise<ExternalCodesBackfillResult> {
  const { data } = await apiClient.post<ExternalCodesBackfillResult>(
    '/admin/entities/external-codes-backfill',
    null,
    { params },
  );
  return data;
}

export async function getExternalCodesBackfillProgress(): Promise<ExternalCodesBackfillProgress> {
  const { data } = await apiClient.get<ExternalCodesBackfillProgress>(
    '/admin/entities/external-codes-backfill-progress',
  );
  return data;
}

/**
 * Entity Mismatch Audit — surfaces misbound announcements + contaminated
 * canonical names. Two-bucket output per the contamination-vs-rebrand
 * doctrine (Bucket A re-point announcement.entity_id, Bucket B direct
 * canonical correction with NO rebrand alias). Operator-driven fixes
 * outside the existing attribute_change_processor approve path.
 */
export interface EntityMismatchAuditResult {
  stats: {
    entities_scanned: number;
    announcements_scanned: number;
    bucket_a_misbinding_candidates: number;
    bucket_a_affected_entities: number;
    bucket_b_canonical_correction_candidates: number;
  };
  bucket_a_misbinding_examples: Array<{
    announcement_id: string;
    announcement_date: string | null;
    announcement_company_name: string;
    bound_entity_id: string;
    bound_canonical_name: string;
    bound_ticker: string | null;
    entity_majority_name: string;
    entity_total_announcements: number;
    entity_fail_guard_count: number;
  }>;
  bucket_b_canonical_correction_examples: Array<{
    entity_id: string;
    ticker: string | null;
    entity_type: string | null;
    stored_canonical_name: string;
    majority_name: string;
    majority_count: number;
    total_announcements: number;
    fail_guard_count: number;
    top_name_distribution: Array<{ name: string; count: number }>;
  }>;
}

export interface EntityMismatchAuditProgress {
  entities_with_announcements: number;
  bound_announcements: number;
  name_mismatch_raw: number;
}

/**
 * External ticker resolution attached to audit examples when
 * with_external_evidence=true. Sourced from marketindex.com.au via
 * FirecrawlService, cached 24h in external_ticker_lookups.
 */
export interface ExternalTickerEvidence {
  ticker: string;
  exchange: string;
  canonical_name: string | null;
  listed_at: string | null;
  status: string | null;
  former_company_names: string[];
  source_url: string;
  fetch_status: string;
  fetch_error: string | null;
  fetched_at: string | null;
  from_cache: boolean;
}

export interface ToolRunSubmission {
  run_id: string;
  tool_name: string;
  dry_run: boolean;
  status: 'submitted';
}

/**
 * Submit a mismatch-audit run. Returns immediately with a run_id; the
 * actual scan runs as a BackgroundTask on assayer-backend and emits
 * tool_status events on the 'assayerai:tools' WebSocket channel.
 * Poll fallback: GET /tools/runs/{run_id}.
 */
export async function submitEntityMismatchAudit(params?: {
  limit?: number;
  with_external_evidence?: boolean;
}): Promise<ToolRunSubmission> {
  const { data } = await apiClient.post<ToolRunSubmission>(
    '/tools/mismatch-audit',
    null,
    { params },
  );
  return data;
}

export async function getEntityMismatchAuditProgress(): Promise<EntityMismatchAuditProgress> {
  const { data } = await apiClient.get<EntityMismatchAuditProgress>(
    '/admin/entities/mismatch-audit-progress',
  );
  return data;
}

/**
 * Phase 4 — Auto-resolve mismatch audit candidates.
 *
 * Two-pass design (4a ticker re-binding + 4b canonical rename) with EEC
 * ground-truth and Phase 1 name-similarity guard. Optional external
 * promotion gate via marketindex lookup (use_external_lookup=true).
 * Dry-run surfaces full proposed SQL; execute applies high + promoted by
 * default; apply_medium opt-in for borderline candidates.
 */
export interface AutoResolveTierCounts {
  total: number;
  high: number;
  promoted: number;
  medium: number;
  low: number;
  applied: number;
  errors: number;
}

export interface AutoResolveStatement {
  db: string;
  sql: string;
  params?: any[];
  description?: string;
}

export interface AutoResolvePlan {
  bucket: 'A' | 'B';
  candidate_id: string;
  tier: 'high' | 'promoted' | 'medium' | 'low';
  reason: string;
  ticker: string | null;
  statements: AutoResolveStatement[];
  internal_evidence: Record<string, any>;
  external_evidence: ExternalTickerEvidence | null;
  applied: boolean;
  apply_error: string | null;
}

export interface EntityMismatchResolveResult {
  stats: {
    bucket_a: AutoResolveTierCounts;
    bucket_b: AutoResolveTierCounts;
  };
  bucket_a_plans: AutoResolvePlan[];
  bucket_b_plans: AutoResolvePlan[];
}

/**
 * Submit a mismatch-resolve run (Phase 4 auto-resolver). Returns a run_id;
 * the resolution runs as a BackgroundTask and emits tool_status events on
 * the 'assayerai:tools' WebSocket channel.
 */
export async function submitEntityMismatchResolve(params?: {
  dry_run?: boolean;
  apply_medium?: boolean;
  use_external_lookup?: boolean;
  limit?: number;
}): Promise<ToolRunSubmission> {
  const { data } = await apiClient.post<ToolRunSubmission>(
    '/tools/mismatch-resolve',
    null,
    { params },
  );
  return data;
}
